import { z } from "zod";
import { SUBSCRIPTION_STATUSES } from "../enums";
import type { AiFeature, AiProvider, Role, SubscriptionStatus } from "../enums";
import { PLAN_INTERVALS } from "./billing";
import type { BillingPaymentDto, BillingPlanDto } from "./billing";

// Platform admin console (WP10): global statistics, organizations, users, plans and platform settings. Only
// platform admins reach these routes; every action is audited.

const oid = z.string().regex(/^[a-f0-9]{24}$/i, "Identifiant invalide");
const count = (label: string, min = 0) => z.number().int(`${label} invalide`).min(min, `${label} invalide`).max(100_000_000_000, `${label} trop grand`);
const boolQuery = z.enum(["true", "false"]).transform((v) => v === "true");

export const adminPlanLimitsSchema = z.object({
  aiTokensMonthly: count("Quota IA"),
  clients: count("Nombre de clients", 1),
  members: count("Nombre de membres", 1),
  conversationsMonthly: count("Conversations"),
  ordersMonthly: count("Commandes"),
  sites: count("Sites"),
  adAccounts: count("Comptes publicitaires"),
});

export const adminPlanFeaturesSchema = z.object({
  byokAllowed: z.boolean().default(false),
  autoReply: z.boolean().default(true),
  mediaBuyer: z.boolean().default(true),
  siteBuilder: z.boolean().default(true),
  customDomains: z.boolean().default(false),
  playbooks: z.boolean().default(true),
  reports: z.boolean().default(true),
});

export const adminPlanInputSchema = z.object({
  code: z
    .string()
    .trim()
    .toLowerCase()
    .min(2, "Code trop court")
    .max(40, "40 caractères maximum")
    .regex(/^[a-z0-9_-]+$/, "Lettres minuscules, chiffres, tirets et soulignés uniquement"),
  name: z.string().trim().min(2, "Nom trop court").max(80, "80 caractères maximum"),
  description: z.string().trim().max(500, "500 caractères maximum").default(""),
  priceMinor: z.number().int("Prix invalide").min(0, "Prix invalide"),
  currency: z.string().trim().length(3, "Devise invalide").toUpperCase().default("TND"),
  interval: z.enum(PLAN_INTERVALS).default("month"),
  trialDays: z.number().int().min(0, "Durée invalide").max(90, "90 jours maximum").default(14),
  limits: adminPlanLimitsSchema,
  features: adminPlanFeaturesSchema.default({}),
  public: z.boolean().default(true),
  active: z.boolean().default(true),
  sortOrder: z.number().int().min(0).max(1000).default(0),
  highlighted: z.boolean().default(false),
});
export type AdminPlanInput = z.infer<typeof adminPlanInputSchema>;

/** The code is the stable identifier (default plan setting): it cannot change after creation. */
export const adminPlanUpdateSchema = z.object({
  name: z.string().trim().min(2, "Nom trop court").max(80, "80 caractères maximum").optional(),
  description: z.string().trim().max(500, "500 caractères maximum").optional(),
  priceMinor: z.number().int("Prix invalide").min(0, "Prix invalide").optional(),
  currency: z.string().trim().length(3, "Devise invalide").toUpperCase().optional(),
  interval: z.enum(PLAN_INTERVALS).optional(),
  trialDays: z.number().int().min(0).max(90, "90 jours maximum").optional(),
  limits: adminPlanLimitsSchema.partial().optional(),
  features: adminPlanFeaturesSchema.partial().optional(),
  public: z.boolean().optional(),
  active: z.boolean().optional(),
  sortOrder: z.number().int().min(0).max(1000).optional(),
  highlighted: z.boolean().optional(),
});
export type AdminPlanUpdate = z.infer<typeof adminPlanUpdateSchema>;

export const adminOrgListQuery = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(25),
  q: z.string().trim().max(120).optional(),
  status: z.enum(["all", "active", "suspended"]).default("all"),
  subscription: z.enum([...SUBSCRIPTION_STATUSES, "all"]).default("all"),
  planId: oid.optional(),
});
export type AdminOrgListQuery = z.infer<typeof adminOrgListQuery>;

export const adminSuspendSchema = z.object({
  reason: z.string().trim().min(3, "Indiquez la raison de la suspension").max(300, "300 caractères maximum"),
});

export const adminChangePlanSchema = z.object({ planId: oid });

export const adminExtendTrialSchema = z.object({
  days: z.number().int("Nombre de jours invalide").min(1, "1 jour minimum").max(90, "90 jours maximum"),
});

export const adminManualPaymentSchema = z.object({
  planId: oid,
  months: z.number().int("Durée invalide").min(1, "1 mois minimum").max(36, "36 mois maximum"),
  amountMinor: z.number().int("Montant invalide").min(0, "Montant invalide"),
  currency: z.string().trim().length(3, "Devise invalide").toUpperCase().default("TND"),
  reference: z.string().trim().max(120, "120 caractères maximum").default(""),
  note: z.string().trim().max(500, "500 caractères maximum").default(""),
});
export type AdminManualPaymentInput = z.infer<typeof adminManualPaymentSchema>;

export const adminAiLimitSchema = z.object({
  monthlyTokenLimit: z.number().int("Quota invalide").min(0, "Quota invalide").max(100_000_000_000, "Quota trop grand").nullable(),
});

export const adminPlatformSettingsSchema = z.object({
  signupsEnabled: z.boolean().optional(),
  defaultPlanCode: z.string().trim().toLowerCase().max(40).nullable().optional(),
});
export type AdminPlatformSettingsInput = z.infer<typeof adminPlatformSettingsSchema>;

export const adminUserListQuery = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(25),
  q: z.string().trim().max(120).optional(),
  status: z.enum(["all", "active", "disabled"]).default("all"),
  admins: boolQuery.optional(),
});
export type AdminUserListQuery = z.infer<typeof adminUserListQuery>;

export const adminUserRoleSchema = z.object({ isPlatformAdmin: z.boolean() });

export interface AdminStatsDto {
  period: string;
  organizations: { total: number; active: number; suspended: number; newThisMonth: number };
  users: { total: number; disabled: number; platformAdmins: number; newThisMonth: number };
  subscriptions: {
    byStatus: Record<SubscriptionStatus, number>;
    byPlan: Array<{ planId: string; code: string; name: string; active: number; trialing: number; pastDue: number }>;
  };
  orders: { total: number; thisMonth: number };
  messages: { total: number; thisMonth: number };
  billing: { paidThisMonth: Array<{ currency: string; amountMinor: number; payments: number }> };
  ai: {
    period: string;
    totalTokens: number;
    calls: number;
    costMicroUsd: number;
    byProvider: Array<{ provider: AiProvider; tokens: number; calls: number; costMicroUsd: number }>;
    byModel: Array<{ provider: AiProvider; model: string; tokens: number; calls: number; costMicroUsd: number }>;
  };
}

export interface AdminOrgRowDto {
  id: string;
  name: string;
  slug: string;
  status: "active" | "suspended";
  ownerEmail: string | null;
  plan: { id: string; code: string; name: string } | null;
  subscription: { status: SubscriptionStatus; trialEndsAt: string | null; currentPeriodEnd: string | null } | null;
  usage: { clients: number; members: number; ordersThisMonth: number; aiTokensThisMonth: number };
  createdAt: string;
}

export interface AdminOrgDetailDto {
  id: string;
  name: string;
  slug: string;
  status: "active" | "suspended";
  suspendedReason: string | null;
  suspendedAt: string | null;
  billingEmail: string | null;
  timezone: string;
  defaultCurrency: string;
  createdAt: string;
  owner: { id: string; name: string; email: string } | null;
  members: Array<{ id: string; userId: string; name: string; email: string; role: Role; status: string; lastLoginAt: string | null }>;
  clients: Array<{ id: string; name: string; status: string; currency: string; createdAt: string }>;
  plan: BillingPlanDto | null;
  subscription: {
    status: SubscriptionStatus;
    trialEndsAt: string | null;
    currentPeriodStart: string | null;
    currentPeriodEnd: string | null;
    cancelAtPeriodEnd: boolean;
    provider: string;
    history: Array<{ event: string; at: string; data: Record<string, unknown> | null }>;
  } | null;
  usage: { clients: number; members: number; conversationsThisMonth: number; ordersThisMonth: number; sites: number; adAccounts: number };
  ai: {
    period: string;
    platformTokens: number;
    byokTokens: number;
    planLimit: number;
    overrideLimit: number | null;
    byFeature: Array<{ feature: AiFeature; tokens: number; calls: number }>;
    byModel: Array<{ provider: AiProvider; model: string; tokens: number; calls: number }>;
  };
  payments: BillingPaymentDto[];
}

export interface AdminUserRowDto {
  id: string;
  name: string;
  email: string;
  status: "active" | "disabled";
  isPlatformAdmin: boolean;
  emailVerified: boolean;
  twoFactorEnabled: boolean;
  organizations: Array<{ id: string; name: string; role: Role }>;
  lastLoginAt: string | null;
  createdAt: string;
}

export interface AdminPlanDto extends BillingPlanDto {
  /** Organizations whose subscription (trialing, active or past due) uses the plan. */
  subscribers: number;
  /** Plan given to new organizations (platform setting, or the built-in trial plan). */
  isDefault: boolean;
}

export interface AdminPlatformSettingsDto {
  signupsEnabled: boolean;
  defaultPlanCode: string | null;
  effectiveDefaultPlan: { code: string; name: string } | null;
}
